import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

const STAGES: Array<{ key: string; label: string; color: string; accent: string }> = [
  { key: 'prospect',  label: 'Prospect',  color: 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200', accent: 'bg-ink-300' },
  { key: 'contacted', label: 'Contacted', color: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',     accent: 'bg-blue-400' },
  { key: 'qualified', label: 'Qualified', color: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200', accent: 'bg-purple-400' },
  { key: 'proposal',  label: 'Proposal',  color: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',   accent: 'bg-amber-500' },
  { key: 'won',       label: 'Won',       color: 'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200',       accent: 'bg-sage-500' },
  { key: 'lost',      label: 'Lost',      color: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',          accent: 'bg-red-400' },
];

/**
 * FR-SALES-04.01-04 — Leads Kanban.
 * Columns per stage, click a card to open detail + advance stage actions.
 */
export function LeadsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['leads'],
    queryFn: () => salesApi.listLeads({ limit: 500 }),
  });

  const byStage: Record<string, any[]> = {};
  for (const s of STAGES) byStage[s.key] = [];
  for (const lead of (listQuery.data ?? [])) {
    (byStage[lead.stage] ?? byStage['prospect']).push(lead);
  }

  // Top-level KPIs
  const totalLeads = listQuery.data?.length ?? 0;
  const openLeads = (byStage['prospect']?.length ?? 0) + (byStage['contacted']?.length ?? 0)
    + (byStage['qualified']?.length ?? 0) + (byStage['proposal']?.length ?? 0);
  const wonValue = (byStage['won'] ?? []).reduce((s, l) => s + parseFloat(l.forecast_value ?? '0'), 0);
  const pipelineValue = ['prospect','contacted','qualified','proposal']
    .flatMap((k) => byStage[k] ?? [])
    .reduce((s, l) => s + parseFloat(l.forecast_value ?? '0'), 0);

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Commerce"
        title="Leads pipeline"
        subtitle="Track prospects through qualification, proposal, and conversion."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New lead
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total leads" value={totalLeads} />
        <KpiTile label="Open" value={openLeads}
          accent={openLeads > 0 ? 'sage' : 'default'} />
        <KpiTile label="Pipeline value" value={fmtMoney(pipelineValue)} />
        <KpiTile label="Won value" value={fmtMoney(wonValue)} accent="sage" />
      </div>

      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : (
        <div className="grid grid-cols-6 gap-3 overflow-x-auto pb-3">
          {STAGES.map((stage) => {
            const leads = byStage[stage.key];
            const totalValue = leads.reduce((s, l) => s + parseFloat(l.forecast_value ?? '0'), 0);
            return (
              <div key={stage.key} className="min-w-[180px]">
                {/* Column header */}
                <div className="card overflow-hidden mb-3">
                  <div className={`h-1 ${stage.accent}`} />
                  <div className="px-3 py-2">
                    <div className="flex justify-between items-center">
                      <div className="eyebrow text-ink-700">{stage.label}</div>
                      <div className="font-mono text-2xs text-ink-500 tabular-nums">
                        {leads.length}
                      </div>
                    </div>
                    {totalValue > 0 && (
                      <div className="font-mono text-xs font-semibold text-ink-900 tabular-nums mt-1">
                        ${totalValue.toFixed(0)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Cards */}
                <div className="space-y-2">
                  {leads.length === 0 ? (
                    <div className="text-2xs text-ink-400 text-center py-4 italic">empty</div>
                  ) : leads.map((l: any) => (
                    <div
                      key={l.id}
                      className={`card p-3 cursor-pointer transition-all hover:shadow-sm ${
                        selected === l.id ? 'ring-2 ring-sage-500 ring-offset-1' : ''
                      }`}
                      onClick={() => setSelected(l.id)}
                    >
                      <div className="text-xs font-semibold text-ink-900 truncate">
                        {l.company ?? l.name}
                      </div>
                      {l.company && (
                        <div className="text-2xs text-ink-500 truncate mt-0.5">{l.name}</div>
                      )}
                      <div className="text-2xs text-ink-500 mt-1.5">
                        {l.assigned_name ?? <span className="italic">unassigned</span>}
                      </div>
                      <div className="flex justify-between items-end mt-2">
                        <div className="font-mono text-xs font-semibold text-ink-900 tabular-nums">
                          ${parseFloat(l.forecast_value ?? '0').toFixed(0)}
                        </div>
                        <div className="text-2xs text-ink-400 font-mono tabular-nums">
                          {parseFloat(l.probability ?? '0').toFixed(0)}%
                        </div>
                      </div>
                      {l.expected_close_date && (
                        <div className="text-2xs text-ink-400 mt-1 font-mono">
                          → {new Date(l.expected_close_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && <LeadDetailModal leadId={selected}
        onClose={() => setSelected(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ['leads'] })} />}

      {showCreate && <CreateLeadModal onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['leads'] });
          setShowCreate(false); setSelected(id);
        }} />}
    </div>
  );
}


function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function LeadDetailModal({ leadId, onClose, onChanged }:
    { leadId: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showConvert, setShowConvert] = useState(false);
  const [showLost, setShowLost] = useState(false);

  const q = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => salesApi.getLead(leadId),
  });

  const advanceMut = useMutation({
    mutationFn: ({ toStage, lostReason }: any) => salesApi.advanceLeadStage(leadId, {
      toStage, lostReason,
    }),
    onSuccess: (d) => {
      toast.success(`Advanced to ${d.toStage}`);
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      onChanged();
      setShowLost(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;
  if (q.error) return <ErrorState error={q.error} />;
  const lead = q.data;

  const canAdvance = !['won', 'lost'].includes(lead.stage);
  const canConvert = !['won', 'lost'].includes(lead.stage) && !lead.converted_customer_id;
  const currentIdx = STAGES.findIndex((s) => s.key === lead.stage);
  const nextStage = currentIdx < STAGES.length - 2 ? STAGES[currentIdx + 1] : null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow mb-1">Sales · Lead</div>
          <h2 className="text-xl font-bold font-display text-ink-900">{lead.company ?? lead.name}</h2>
          {lead.company && <div className="text-sm text-ink-500 mt-0.5">{lead.name}</div>}
          <div className="flex items-center gap-2 mt-2">
            <StageBadge stage={lead.stage} />
            <span className="text-xs text-ink-500">
              {parseFloat(lead.probability ?? '0').toFixed(0)}% probability
            </span>
          </div>
        </div>
        <button
          className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-ink-900 hover:bg-canvas-subtle rounded-md transition-colors flex-shrink-0"
          onClick={onClose} aria-label="Close"
        >✕</button>
      </div>

      <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm">
          {lead.email && (
            <div><span className="eyebrow text-ink-400 mr-1">Email</span>
              <span className="text-ink-700">{lead.email}</span></div>
          )}
          {lead.phone && (
            <div><span className="eyebrow text-ink-400 mr-1">Phone</span>
              <span className="text-ink-700">{lead.phone}</span></div>
          )}
          {lead.source && (
            <div><span className="eyebrow text-ink-400 mr-1">Source</span>
              <span className="text-ink-700">{lead.source}</span></div>
          )}
          <div><span className="eyebrow text-ink-400 mr-1">Forecast</span>
            <span className="font-mono font-semibold text-ink-900">
              ${parseFloat(lead.forecast_value ?? '0').toFixed(2)}
            </span>
          </div>
          {lead.expected_close_date && (
            <div><span className="eyebrow text-ink-400 mr-1">Expected close</span>
              <span className="text-ink-700">
                {new Date(lead.expected_close_date).toLocaleDateString()}
              </span>
            </div>
          )}
          <div><span className="eyebrow text-ink-400 mr-1">Assigned</span>
            <span className="text-ink-700">{lead.assigned_name ?? '—'}</span>
          </div>
        </div>

        {lead.notes && (
          <div className="bg-canvas-subtle rounded-xl px-4 py-3 text-sm text-ink-700 leading-relaxed mb-4 whitespace-pre-line">
            {lead.notes}
          </div>
        )}

        {lead.converted_customer_name && (
          <div className="bg-sage-50 border border-sage-200 text-sage-800 text-xs p-3 rounded-xl mb-3">
            ✓ Converted to customer <strong>{lead.converted_customer_name}</strong>
          </div>
        )}
        {lead.lost_reason && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-xl mb-3">
            Lost: {lead.lost_reason}
          </div>
        )}

        {lead.history && lead.history.length > 0 && (
          <div className="mb-2">
            <div className="eyebrow text-ink-500 mb-2">Stage history</div>
            <ul className="space-y-1.5">
              {lead.history.map((h: any) => (
                <li key={h.id} className="text-xs text-ink-600">
                  <span className="font-mono text-2xs text-ink-400">
                    {new Date(h.changed_at).toLocaleString()}
                  </span>
                  {' '}{h.from_stage ? `${h.from_stage} → ${h.to_stage}` : `created as ${h.to_stage}`}
                  {h.changed_by_name && ` · ${h.changed_by_name}`}
                  {h.notes && <div className="text-2xs text-ink-400 ml-4 mt-0.5">{h.notes}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 flex-wrap justify-end flex-shrink-0">
        {canAdvance && nextStage && (
          <button className="btn-primary" disabled={advanceMut.isPending}
            onClick={() => advanceMut.mutate({ toStage: nextStage.key })}>
            Advance to {nextStage.label}
          </button>
        )}
        {canAdvance && (
          <button className="btn-secondary" disabled={advanceMut.isPending}
            onClick={() => setShowLost(true)}>
            Mark lost
          </button>
        )}
        {canConvert && (
          <button className="btn-primary" onClick={() => setShowConvert(true)}>
            Convert to customer
          </button>
        )}
      </div>

      {showConvert && (
        <ConvertModal leadId={leadId} leadName={lead.company ?? lead.name}
          onClose={() => setShowConvert(false)}
          onConverted={() => {
            setShowConvert(false);
            onChanged();
            qc.invalidateQueries({ queryKey: ['lead', leadId] });
          }} />
      )}
      {showLost && (
        <MarkLostDialog
          loading={advanceMut.isPending}
          onClose={() => setShowLost(false)}
          onConfirm={(reason) => advanceMut.mutate({ toStage: 'lost', lostReason: reason })}
        />
      )}
    </Modal>
  );
}

function MarkLostDialog({ loading, onClose, onConfirm }: {
  loading: boolean; onClose: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Sales · Lead</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Mark as lost?</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          The lead will be closed. A lost reason is required for pipeline reporting.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Lost reason <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Chose competitor, budget cut, no response" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}>
          {loading ? 'Saving…' : 'Mark lost'}
        </button>
      </div>
    </Modal>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const s = STAGES.find((x) => x.key === stage);
  return <span className={`badge ${s?.color ?? 'bg-canvas-subtle'} text-xs`}>{s?.label ?? stage}</span>;
}

function CreateLeadModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('');
  const [forecastValue, setForecastValue] = useState(0);
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => salesApi.createLead({
      name, company: company || undefined,
      email: email || undefined, phone: phone || undefined,
      source: source || undefined,
      forecastValue,
      expectedCloseDate: expectedCloseDate || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => { toast.success('Lead created'); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Sales · Pipeline</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New lead</h2>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
        <div>
          <label className="label">Contact name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">Company <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} />
        </div>
        <div>
          <label className="label">Email</label>
          <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">Phone</label>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label className="label">Source</label>
          <input className="input" value={source} onChange={(e) => setSource(e.target.value)}
            placeholder="referral, web, cold call" />
        </div>
        <div>
          <label className="label">Forecast value</label>
          <input type="number" min={0} step={100} className="input tabular-nums" value={forecastValue}
            onChange={(e) => setForecastValue(parseFloat(e.target.value) || 0)} />
        </div>
        <div className="col-span-2">
          <label className="label">Expected close date</label>
          <input type="date" className="input" value={expectedCloseDate}
            onChange={(e) => setExpectedCloseDate(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create lead'}
        </button>
      </div>
    </Modal>
  );
}

function ConvertModal({ leadId, leadName, onClose, onConverted }:
    { leadId: string; leadName: string; onClose: () => void; onConverted: () => void }) {
  const [customerCode, setCustomerCode] = useState(
    leadName.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) || 'CUST'
  );
  const [customerType, setCustomerType] = useState<'individual' | 'business'>('business');
  const [creditLimit, setCreditLimit] = useState(0);

  const mut = useMutation({
    mutationFn: () => salesApi.convertLead(leadId, { customerCode, customerType, creditLimit }),
    onSuccess: (d) => { toast.success(`Converted to customer ${d.customerCode}`); onConverted(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Sales · Convert lead</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Convert to customer</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          Creates a new customer record. The lead will be marked as won.
        </p>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div>
          <label className="label">Customer code</label>
          <input className="input font-mono uppercase" value={customerCode}
            onChange={(e) => setCustomerCode(e.target.value.toUpperCase())} />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={customerType} onChange={(e) => setCustomerType(e.target.value as any)}>
            <option value="business">Business</option>
            <option value="individual">Individual</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Credit limit</label>
          <input type="number" min={0} step={100} className="input tabular-nums" value={creditLimit}
            onChange={(e) => setCreditLimit(parseFloat(e.target.value) || 0)} />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!customerCode || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Converting…' : 'Convert lead'}
        </button>
      </div>
    </Modal>
  );
}
